import type { BatchOutcome } from "../lib/types";
import { Alert } from "./ui";

/**
 * What a batch from a queue did: how many moved, then why the rest did not.
 *
 * Reasons rather than a count of failures, because "three failed" is not
 * something an operator can act on — and deduplicated, so twenty cases a
 * colleague closed a moment ago say so once rather than twenty times. The same
 * reading the free-inference queue gives its batches, shared by the others.
 */
export function BatchResult({
  outcomes,
  noun,
  done,
}: {
  outcomes: BatchOutcome[];
  /** What a row is — "case", "ticket", "request". */
  noun: string;
  /** What happened to the ones that moved, past tense — "snoozed", "resolved". */
  done: string;
}) {
  const moved = outcomes.filter((outcome) => outcome.ok).length;
  const reasons = [
    ...new Set(outcomes.filter((outcome) => !outcome.ok).map((outcome) => outcome.error ?? "Not moved.")),
  ];

  return (
    <Alert tone={reasons.length === 0 ? "success" : "warning"}>
      <p>
        <strong className="tabular-nums">{moved}</strong> of {outcomes.length} {noun}
        {outcomes.length === 1 ? "" : "s"} {done}.
        {moved < outcomes.length && ` ${outcomes.length - moved} left as they were:`}
      </p>
      {reasons.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs">
          {reasons.map((reason) => (
            <li key={reason}>· {reason}</li>
          ))}
        </ul>
      )}
    </Alert>
  );
}
